import banner from "../../../courses/module-1/img6.png";

const modulo6 = {
  courseId: "finanzas-personales",
  id: "modulo-6",
  title: "📘 MODULE 6: Responsible Consumption and Financial Habits",
  estimatedDuration: "2 hours",
  format: "Reading + self-observation + written challenge",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objectives",
      title: "🎯 Module objectives",
      content: [
        "Identify how and why we make consumption decisions without thinking.",
        "Recognize the cumulative effect of small, repetitive expenses.",
        "Develop conscious and sustainable financial habits.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Main reading",
      title: "📖 Main reading: What is responsible consumption?",
      content: [
        "Consuming responsibly does not mean stopping buying things, but doing it with awareness and purpose.",
        "It means asking yourself:",
        {
          type: "list",
          items: [
            "Do I really need this?",
            "Can I pay for it without going into debt or affecting my goals?",
            "Am I buying this out of impulse, pressure, or boredom?",
          ],
        },
        "Intentional consumption = taking care of your money and your decisions.",
      ],
    },
    {
      id: "razones",
      navLabel: "Reasons",
      title: "🚩 Reasons to adopt conscious consumption",
      content: [
        {
          type: "list",
          items: [
            "You avoid post-purchase regret",
            "You reduce waste and trend-based consumption",
            "You strengthen your financial control without feeling like you are “depriving yourself”",
            "You make decisions aligned with your values",
          ],
        },
      ],
    },
    {
      id: "gasto-hormiga",
      navLabel: "Small recurring expenses",
      title: "💸 The impact of small recurring expenses",
      content: [
        "Small recurring expenses are daily expenses that seem harmless, but silently add up. Examples:",
        {
          type: "table",
          headers: [
            "Expense",
            "Approx. monthly expense",
            "Approx. annual expense",
          ],
          rows: [
            ["$40 coffee", "$800", "$9,600"],
            ["$30 snacks", "$600", "$7,200"],
            ["Delivery apps", "$1,000", "$13,000"],
          ],
        },
        "Quick exercise:",
        {
          type: "list",
          items: [
            "Write down EVERYTHING you spend without thinking for 3 days.",
            "At the end, add it up and identify which expenses you could reduce without pain.",
          ],
        },
      ],
    },
    {
      id: "tecnicas",
      navLabel: "Techniques",
      title: "🛠 Techniques to improve your money habits",
      content: [
        {
          type: "orderedList",
          items: [
            "The 10-second rule: Before buying, count to 10 and ask yourself if you need it.",
            "The 72-hour waiting list: If it is not urgent, wait 3 days. If you still want it, buy it intentionally.",
            "Treat budget: It is not about eliminating pleasures, but about defining how much you can enjoy without affecting your goals.",
            "Apps to track habits: TryHabit, Tally, simple Google Sheets.",
            "Visual bill technique: Put your written savings goal inside your wallet.",
          ],
        },
      ],
    },
    {
      id: "herramienta",
      navLabel: "Tool",
      title: "🧩 Module tool: financial habits tracking sheet",
      content: [
        "To accompany this module, use a weekly tracking sheet where you will write down:",
        {
          type: "list",
          items: [
            "Expense avoided",
            "Reason for the impulse",
            "Emotion present",
            "Final decision: did you buy it or not?",
            "What did you learn about yourself that day?",
          ],
        },
        "🔗 You can easily create it on paper, in Google Sheets, or with any notes app.",
        "You can also use the same format to track positive habits, such as saving, writing down expenses, or not using your card.",
      ],
    },
    {
      id: "ejemplo",
      navLabel: "Real example",
      title: "💬 Real example",
      content: [
        "🎓 Alondra, 19, from Guadalajara, realized she was spending $150 a week on Uber when she could walk or take the bus.",
        "She decided to try a “week without unnecessary Uber rides” and managed to save $600 that month.",
        "With that money, she paid for her web domain to launch a creative portfolio as a designer.",
        "“It was not about stopping myself from living. It was about choosing better what I spent the money I work so hard to earn on.”",
      ],
    },
    {
      id: "maslow",
      navLabel: "Maslow",
      title: "🔺 Extra: Maslow’s Pyramid and our consumption decisions",
      content: [
        {
          type: "paragraph",
          text: "📌 What is Maslow’s pyramid?",
        },
        "It is a psychological model that represents human needs organized in levels, from the most basic to the most complex. It helps us understand why we act the way we do, including how we spend our money.",
        {
          type: "table",
          headers: ["Level", "Human need", "Related example"],
          rows: [
            [
              "1. Physiological",
              "Food, water, rest",
              "Buying food, transportation",
            ],
            [
              "2. Safety",
              "Stability, health, money",
              "Savings, insurance, rent",
            ],
            [
              "3. Social",
              "Belonging, friends",
              "Brand-name clothes, outings, trendy gadgets",
            ],
            [
              "4. Esteem",
              "Success, image, prestige",
              "Accessories, expensive technology, personal style",
            ],
            [
              "5. Self-actualization",
              "Achievement, purpose, freedom",
              "Education, entrepreneurship, experiences",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "🤔 What does this have to do with your finances?",
        },
        {
          type: "paragraph",
          text: "A lot! Many times we do not spend because of a real need, but because we want to:",
        },
        {
          type: "list",
          items: [
            "Feel like we “fit in” (level 3)",
            "Show success or self-esteem (level 4)",
            "Cover emotions or insecurities",
          ],
        },
        {
          type: "paragraph",
          text: "💡 Impulsive consumption often tries to fill gaps in higher levels without properly covering the basic ones.",
        },
      ],
    },
    {
      id: "evaluacion",
      navLabel: "Final challenge",
      title: "📝 Assessment: Final course challenge",
      content: [
        'Written challenge: "My week without unnecessary expenses" in your notebook or document',
        "What should you do?",
        "For 7 days:",
        {
          type: "orderedList",
          items: [
            "Observe your daily consumption habits.",
            "Try to avoid at least one unnecessary expense each day.",
            "Record your decisions, either on paper, in an app, or as a voice note.",
            "Reflect:",
          ],
        },
        {
          type: "list",
          items: [
            "What did you learn about yourself?",
            "What was the hardest expense to avoid?",
            "Did you feel deprived or empowered?",
            "How much were you able to save?",
          ],
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliography",
      title: "📚 Recommended bibliography",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista: Finanzas personales para jóvenes”",
        "Daniel Kahneman – “Thinking, Fast and Slow”",
        "Blog: “Minimalismo Financiero” – finanzaszen.mx",
        "Podcast: “Neurona Financiera” – conscious habits + money",
      ],
    },
    {
      id: "frase",
      navLabel: "Quote of the day",
      title: "🧠 Quote of the day",
      content: [
        "“It is not about spending less. It is about spending better.”",
      ],
    },
  ],
};

export default modulo6;
